import { useState, useEffect, useCallback } from 'react';
import { GridApiPremium } from '@mui/x-data-grid-premium';
import Actions from '../../../store/Actions';
import {
  WizardSessionListItem,
  UploadedFileInfo,
} from '../../../types/generatedTypes';
import { DataFeedRow } from '../types';
import { getStepLabel } from '../../DataFeedWizard/stepLabels';

interface UseDataFeedDataProps {
  apiRef: React.RefObject<GridApiPremium | null>;
}

export const useDataFeedData = ({ apiRef }: UseDataFeedDataProps) => {
  const [data, setData] = useState<DataFeedRow[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [autosizeColumns, setAutosizeColumns] = useState(false);

  // Handle autosize state changes - dynamically get columns from grid API
  useEffect(() => {
    if (autosizeColumns && apiRef.current) {
      const allColumns = apiRef.current.getAllColumns();
      const internalColumns = ['__check__', '__tree_data_group__'];
      const visibleUserColumns = allColumns
        .filter(col => !internalColumns.includes(col.field))
        .map(col => col.field);

      if (visibleUserColumns.length > 0) {
        apiRef.current.autosizeColumns({
          includeHeaders: true,
          includeOutliers: true,
          expand: true,
          columns: visibleUserColumns,
        });
      }
    } else if (!autosizeColumns && apiRef.current) {
      const allColumns = apiRef.current.getAllColumns();
      const internalColumns = ['__check__', '__tree_data_group__'];
      allColumns.forEach(col => {
        if (!internalColumns.includes(col.field)) {
          apiRef.current?.setColumnWidth(col.field, 0);
        }
      });
    }
  }, [autosizeColumns, apiRef]);

  const fetchSessions = useCallback(async () => {
    try {
      setLoading(true);
      const response = await Actions.listWizardSessions(true);

      if (!response || !response.sessions) {
        setData([]);
        return;
      }

      const mappedData: DataFeedRow[] = response.sessions.map(
        (session: WizardSessionListItem, index: number) => {
          const uploadedFiles: UploadedFileInfo[] =
            session.uploaded_files || [];
          const hasVaultFiles = uploadedFiles.some(
            f => f.upload_tab === 'vaults'
          );

          return {
            id: index + 1,
            sessionId: session.session_id,
            flowName: session.name || null,
            fileType: hasVaultFiles ? 'Vault' : 'Excel',
            fileName: session.file_name || '-',
            uploadDate: session.updated_at,
            createdAt: session.created_at,
            status: session.is_complete
              ? 'Uploaded'
              : getStepLabel(session.current_step),
            currentStep: session.current_step,
            rowCount: session.row_count || 0,
            enabledRowsCount: session.enabled_rows_count || 0,
            uploadedRowsCount: session.uploaded_rows_count || 0,
            isComplete: session.is_complete,
            uploadedFiles,
          };
        }
      );

      setData(mappedData);
    } catch (error) {
      console.error('Failed to fetch wizard sessions:', error);
      setData([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSessions();
  }, [fetchSessions]);

  return {
    data,
    loading,
    refetch: fetchSessions,
    autosizeColumns,
    setAutosizeColumns,
  };
};
